/**
 * Single app card — status dot + slug + badges + health/access/cert rows + actions.
 *
 * - Pulse animation on healthy status only.
 * - Probe button triggers ztnaApi.probeApp mutation.
 * - "Detalhes" opens AppDrawer via parent callback.
 * - "Audit" links to existing /auditoria-global filtered by resource_id.
 */
import { useEffect, useRef } from 'react'
import { Badge, Button, Group, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconBolt,
  IconHistory,
  IconArrowRight,
  IconPlayerPlay,
} from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { ztnaApi } from '../api'
import { foiParaGmud, notifyPendingGmud } from '../gmudNotice'
import {
  type AppHealthSummary,
  deriveHealthBucket,
  type HealthBucket,
} from '../types'
import styles from '../ZtnaAdmin.module.css'
import { formatApiError } from '@/api/errors'

interface Props {
  app: AppHealthSummary
  onSelect: (app: AppHealthSummary) => void
  /** Destaca o card recém-provisionado (scroll + pulse + badge NOVO). */
  highlight?: boolean
}

function healthLabel(b: HealthBucket): string {
  return ({ healthy: 'Healthy', degraded: 'Degraded', failed: 'Failed', idle: 'Idle' } as const)[b]
}

function healthColor(b: HealthBucket): string {
  return (
    {
      healthy: 'var(--ai-success, #2f9e44)',
      degraded: 'var(--ai-warning, #f59f00)',
      failed: 'var(--ai-danger, #e03131)',
      idle: 'var(--ai-text-muted, #adb5bd)',
    } as const
  )[b]
}

export function envBadgeColor(env: string): string {
  switch (env.toUpperCase()) {
    case 'PRD': return 'red'
    case 'HML': return 'inovareTeal'
    case 'QA':  return 'yellow'
    case 'DEV': return 'gray'
    default:    return 'gray'
  }
}

/** Título do selo de DNS, com a hora da medição. Valor sem data envelhece em
 *  silêncio — foi o defeito do `smoke_intercepted_by_broker` na app 87. */
function tituloDoAlvoDns(quando: string | null, frase: string): string {
  if (!quando) return frase
  return `${frase} Medido em ${dayjs(quando).format('DD/MM/YYYY HH:mm')}.`
}


export function AppCard({ app, onSelect, highlight = false }: Props) {
  const bucket = deriveHealthBucket(app)
  const qc = useQueryClient()
  const cardRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (highlight && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [highlight])
  const probeMutation = useMutation({
    mutationFn: () => ztnaApi.probeApp(app.id),
    onSuccess: (data) => {
      notifications.show({
        color: data.ok ? 'teal' : 'red',
        title: data.ok ? 'Probe OK' : 'Probe falhou',
        message: data.ok
          ? `${data.http_status ?? '—'} · ${data.latency_ms ?? '—'}ms`
          : data.error ?? 'Falha desconhecida',
      })
    },
    onError: (err: any) => {
      const status = err?.response?.status
      notifications.show({
        color: 'red',
        title: 'Erro no probe',
        message: status === 429 ? 'Rate limit (1 probe/30s)' : 'Falha ao executar probe',
      })
    },
  })
  const resumeMutation = useMutation({
    mutationFn: () => ztnaApi.toggleApp(app.id, true),
    onSuccess: (resp) => {
      // Em ambiente produtivo a reativacao vira GMUD e a app continua
      // suspensa. Dizer "voltou ao ar" aqui seria afirmar uma mudanca que
      // nao aconteceu.
      if (foiParaGmud(resp)) {
        notifyPendingGmud('a reativação', app.display_name || app.slug, resp.gmud_id)
        return
      }
      notifications.show({
        color: 'teal',
        title: 'App reativada',
        message: `${app.display_name || app.slug} voltou ao ar.`,
      })
      qc.invalidateQueries({ queryKey: ['ztna', 'apps'] })
      qc.invalidateQueries({ queryKey: ['ztna', 'kpis'] })
    },
    onError: (err: unknown) => {
      // TASK-10087: a causa CHEGA no browser e a tela jogava fora. Em 24/08 o
      // 409 `reztna_before_resume_required` virou "Falha ao reativar a app." e
      // o operador ficou duas horas sem saber que precisava de re-ZTNA.
      notifications.show({
        color: 'red',
        title: 'Erro ao reativar',
        message: formatApiError(err, 'Falha ao reativar a app.'),
      })
    },
  })

  const lastSeen = app.last_probed_at
    ? dayjs(app.last_probed_at).format('HH:mm:ss')
    : 'sem probes'

  // Três estados, três símbolos. O ✓ é reservado a certificado conhecido e longe
  // de vencer — ausência de dado não é aprovação (TASK-9795: o card exibia
  // "✓ sem cert ACM", inclusive em app com health Failed).
  const certConhecido = Boolean(app.cert_expires_at)
  const certText = certConhecido
    ? `válido até ${dayjs(app.cert_expires_at).format('YYYY-MM-DD')}`
    : 'sem cert ACM'
  const certWarn =
    certConhecido && dayjs(app.cert_expires_at).diff(dayjs(), 'day') < 30
  const certSimbolo = !certConhecido ? '—' : certWarn ? '⚠' : '✓'

  // TASK-10087: quatro estados, nao dois. `enabled` sozinho chamava de
  // "PAUSADO" uma app FORA do broker, servida sem autenticacao — que se le como
  // protegida, porem desligada. Medido em 24/08 na app ticket.dev.commerce.
  //
  // `open` sem `auth_bypass` e' o modo dns_only: o DNS aponta para a origem e o
  // trafego nao passa pelo broker. `open` COM `auth_bypass` ainda passa pelo
  // broker, so com a autenticacao desligada. Sao riscos diferentes.
  const postura = (() => {
    if (app.access_mode === 'open') {
      return app.auth_bypass
        ? {
            rotulo: 'ABERTA — sem autenticação',
            cor: 'red',
            titulo: 'Passa pelo broker, mas a autenticação está DESLIGADA: qualquer pessoa com a URL entra.',
          }
        : {
            rotulo: 'FORA DO ZTNA — DNS na origem',
            cor: 'red',
            titulo: 'O DNS aponta para a origem: o tráfego NÃO passa pelo broker e não há autenticação nenhuma.',
          }
    }
    if (app.enabled) {
      return {
        rotulo: 'ZTNA ATIVO',
        cor: 'green',
        titulo: 'App protegida pelo broker ZTNA',
      }
    }
    return {
      rotulo: 'ZTNA PAUSADO',
      cor: 'orange',
      titulo: 'ZTNA pausado — broker retorna 404 até retomada',
    }
  })()
  // Mantido para a moldura do card: distingue "operando" de "nao operando".
  const ztnaActive = app.enabled
  return (
    <article
      ref={cardRef}
      className={highlight ? `${styles.appCard} ${styles.appCardNew}` : styles.appCard}
      aria-label={`app ${app.slug}`}
      style={{
        opacity: ztnaActive ? 1 : 0.78,
        borderLeft: ztnaActive
          ? '3px solid var(--ai-success, #2f9e44)'
          : '3px solid var(--ai-warning, #f59f00)',
      }}
    >
      <div className={styles.appCardHead}>
        <span
          className={`${styles.statusDot} ${styles[bucket]}`}
          aria-label={`status: ${healthLabel(bucket)}`}
          role="img"
        />
        <span className={styles.appCardName} title={app.slug}>
          {app.display_name || app.slug}
        </span>
        <Group gap={4} ml="auto">
          {highlight && (
            <Badge size="sm" color="teal" variant="light" title="Provisionado agora">
              NOVO
            </Badge>
          )}
          <Badge
            size="sm"
            color={postura.cor}
            variant="filled"
            title={postura.titulo}
          >
            {postura.rotulo}
          </Badge>
          {app.smoke_intercepted_by_broker === false && (
            <Badge
              size="sm"
              color="red"
              variant="filled"
              title="Smoke recebeu resposta do upstream sem passar pelo broker — provável DNS mal configurado. Validar Route53."
            >
              SMOKE WARN
            </Badge>
          )}
          {/* Para onde o DNS aponta. Os TRÊS estados aparecem, e "não medido" é
              texto: um selo apagado seria lido como "sem problema", que é
              justamente a leitura errada — foi assim que cinco apps ficaram
              `enforced` na tela com o DNS fora do broker. */}
          {app.dns_target_check === 'fora' && (
            <Badge
              size="sm"
              color="red"
              variant="filled"
              title={tituloDoAlvoDns(app.dns_target_checked_at,
                'O hostname não resolve no balanceador da plataforma, então o broker não está no caminho. Não diz para onde ele aponta — conferir o registro na conta do tenant.')}
            >
              DNS FORA
            </Badge>
          )}
          {app.dns_target_check === 'plataforma' && (
            <Badge
              size="sm"
              color="teal"
              variant="light"
              title={tituloDoAlvoDns(app.dns_target_checked_at,
                'O hostname resolve no balanceador da plataforma.')}
            >
              DNS OK
            </Badge>
          )}
          {/* Nasceu fora do pipeline: `ztna_provision_jobs` vazio. Sem isto, a
              app parece normal na lista e só se descobre o problema ao tentar
              mexer nela — foi o que travou quatro tentativas na app 87. */}
          {!app.tem_historico && app.discarded_at === null && (
            <Badge
              size="sm"
              color="grape"
              variant="light"
              title="Criada fora do pipeline de provisionamento: não tem histórico, então excluir, pausar e reprovisionar são recusados. Use Adotar para produzir o histórico, ou Descartar do catálogo para tirá-la da lista sem tocar em AWS."
            >
              FORA DO PIPELINE
            </Badge>
          )}
          {app.discarded_at !== null && (
            <Badge size="sm" color="gray" variant="outline" title="Descartada do catálogo. O DNS, o certificado e a regra do balanceador continuam onde estavam.">
              DESCARTADA
            </Badge>
          )}
          {app.dns_target_check === null && (
            <Badge size="sm" color="gray" variant="outline" title="A sonda não conseguiu resolver o hostname ou o balanceador da plataforma. Nada foi medido — isto não é 'sem problema'.">
              DNS NÃO MEDIDO
            </Badge>
          )}
          <Badge size="sm" color={envBadgeColor(app.environment)} variant="light">
            {app.environment}
          </Badge>
          <Badge size="sm" color="gray" variant="light">
            {app.routing_mode.toUpperCase()}
          </Badge>
          <Badge size="sm" color="inovareTeal" variant="light">
            {app.identity_mode.toUpperCase()}
          </Badge>
        </Group>
      </div>
      <div className={styles.appCardHostname} title={app.hostname}>
        {app.hostname}
      </div>
      <Stack gap={2} mt={4}>
        <Group gap="xs" wrap="nowrap">
          <Text size="xs" c="dimmed" w={70}>Health</Text>
          <Text size="xs" fw={600} style={{ color: healthColor(bucket) }}>
            ● {healthLabel(bucket)}
          </Text>
          <Text size="xs" c="dimmed" ff="monospace">
            {app.last_latency_ms ? `· ${app.last_latency_ms}ms` : ''} · last seen {lastSeen}
          </Text>
        </Group>
        <Group gap="xs" wrap="nowrap">
          <Text size="xs" c="dimmed" w={70}>Cert ACM</Text>
          <Text
            size="xs"
            ff="monospace"
            c={!certConhecido ? 'dimmed' : undefined}
            style={{ color: certWarn ? 'var(--ai-warning)' : undefined }}
          >
            {certSimbolo} {certText}
          </Text>
        </Group>
      </Stack>
      <Group gap="xs" mt={6}>
        {ztnaActive ? (
          <Button
            size="xs"
            variant="subtle"
            color="inovareTeal"
            leftSection={<IconBolt size={14} />}
            loading={probeMutation.isPending}
            onClick={() => probeMutation.mutate()}
          >
            Probe
          </Button>
        ) : (
          <Button
            size="xs"
            variant="filled"
            color="teal"
            leftSection={<IconPlayerPlay size={14} />}
            loading={resumeMutation.isPending}
            onClick={() => resumeMutation.mutate()}
          >
            Reativar
          </Button>
        )}
        <Button
          size="xs"
          variant="subtle"
          color="gray"
          leftSection={<IconHistory size={14} />}
          component="a"
          href={`/auditoria-global?resource_type=application&resource_id=${app.id}`}
        >
          Audit
        </Button>
        <Button
          size="xs"
          variant="default"
          rightSection={<IconArrowRight size={14} />}
          onClick={() => onSelect(app)}
          ml="auto"
        >
          Detalhes
        </Button>
      </Group>
    </article>
  )
}

export default AppCard
